import { RouteName } from "@/router/routeName";
import type { MoonRouteMeta } from "@/models/moonRouteMeta";
import { UserPermission } from "@/models/userPermission";

interface Dictionary<T> {
  [Key: string]: T;
}

export default (routeName: RouteName): MoonRouteMeta => {
  const mapping: Dictionary<MoonRouteMeta> = {
    [RouteName.AUTH]: {
      requiresAuth: false,
      requiredPermissions: [],
    },
    [RouteName.LOGIN]: {
      requiresAuth: false,
      requiredPermissions: [],
    },
    [RouteName.MFA]: {
      requiredPermissions: [UserPermission.usePasswordlessCredential],
    },
    [RouteName.RECOVERY_CODE_LOGIN]: {
      requiredPermissions: [UserPermission.useRecoveryCode],
    },
    [RouteName.USER_ONBOARD]: {
      requiresAuth: false,
      requiredPermissions: [],
    },
    [RouteName.PASSWORD_RESET_REQUEST]: {
      requiresAuth: false,
      requiredPermissions: [],
    },
    [RouteName.PASSWORD_RESET]: {
      requiresAuth: false,
      requiredPermissions: [],
    },
    [RouteName.MAGIC_LINK]: {
      requiresAuth: false,
      requiredPermissions: [],
    },
    [RouteName.DASHBOARD]: {
      requiredPermissions: [],
    },
    [RouteName.ORGANISATIONS]: {
      requiredPermissions: [UserPermission.orgList],
    },
    [RouteName.ORGANISATION_LIST]: {
      requiredPermissions: [UserPermission.orgList],
    },
    [RouteName.ORGANISATION_NEW]: {
      requiredPermissions: [UserPermission.orgList, UserPermission.orgCreate],
    },
    [RouteName.ORGANISATION_EDIT]: {
      requiredPermissions: [UserPermission.orgList, UserPermission.orgEdit],
    },
    [RouteName.SYSTEM_CONTENT]: {
      requiredPermissions: [UserPermission.orgList, UserPermission.orgEdit],
    },
    [RouteName.SCENARIO_TREES]: {
      requiredPermissions: [UserPermission.orgList, UserPermission.orgEdit],
    },
    [RouteName.SERVICE_PLANS]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.SERVICE_PLAN_LIST]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.SERVICE_PLAN_EDIT]: {
      requiredPermissions: [UserPermission.orgList, UserPermission.orgEdit],
    },
    [RouteName.SERVICE_PLAN_CREATE]: {
      requiredPermissions: [UserPermission.orgCreate, UserPermission.orgEdit],
    },
    [RouteName.SERVICE_PLAN_TRANSFER_GROUPS]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.SUITE_STATE]: {
      requiredPermissions: [UserPermission.orgList, UserPermission.orgEdit],
    },
    [RouteName.SUITE_STATE_LIST]: {
      requiredPermissions: [UserPermission.orgList, UserPermission.orgEdit],
    },
    [RouteName.SUITE_STATE_UPDATE]: {
      requiredPermissions: [UserPermission.orgList, UserPermission.orgEdit],
    },
    [RouteName.USER]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.USER_EDIT]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.USER_SUBMIT_SCORE]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.USER_SCORE_REPORTS]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.USER_SCORE_REPORT]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.HEATMAP_VISUALISER]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.AUDIT]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.APP_VERSION]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.DATA_OPERATIONS]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.ANALYTICS]: {
      requiredPermissions: [UserPermission.viewAnalytics],
    },
    [RouteName.ANALYTICS_OVERVIEW]: {
      requiredPermissions: [UserPermission.viewAnalytics],
    },
    [RouteName.ANALYTICS_LEADERBOARD]: {
      requiredPermissions: [UserPermission.viewAnalytics],
    },
    [RouteName.USERS]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.ADD_USERS]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.GROUPS]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.GROUP_LIST]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.SUITE_MANAGEMENT]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.GROUP_EDIT]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.GROUP_CREATE]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.GROUP_ASSIGN_SUITES]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.GROUP_USERS]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.TRENDS_ANALYTICS]: {
      requiredPermissions: [
        UserPermission.scoreList,
        UserPermission.userListUnder,
      ],
    },
    [RouteName.LEARNING_MATERIALS]: {
      requiredPermissions: [],
    },
    [RouteName.REPORT_GENERATOR]: {
      requiredPermissions: [
        UserPermission.scoreList,
        UserPermission.userListUnder,
      ],
    },
    [RouteName.MY_PROFILE]: {
      requiredPermissions: [],
    },
    [RouteName.MOONBOARDING]: {
      requiredPermissions: [],
    },
    [RouteName.MY_LEARNING]: {
      requiredPermissions: [],
    },
    [RouteName.USER_MODULE_STATS]: {
      requiredPermissions: [],
    },
    [RouteName.LEADERBOARD]: {
      requiredPermissions: [UserPermission.userListUnder],
    },
    [RouteName.ORGANISATION_LOGIN]: {
      requiresAuth: false,
      requiredPermissions: [],
    },
    [RouteName.PLATFORM_ISSUES]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    [RouteName.SYSTEM]: {
      requiredPermissions: [UserPermission.superAdmin],
    },
    // We only allow 'authorised' users to get 404 pages. Unauthorised users who are hitting a 404 route
    // will get re-directed to the login page instead. Provides a soft avoidance of route enumeration
    // (JS bundles can be inspected to work out what routes are doing)
    [RouteName.NOT_FOUND]: {
      requiresAuth: true,
      requiredPermissions: [],
    },
    [RouteName.DEV_ICONS]: {
      requiresAuth: false,
      requiredPermissions: [],
    },
  };

  return mapping[routeName];
};
